import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  HighlightOff as HighlightOffIcon,
} from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/Info';

import { SectionWrapper, ToggleTickSwitch, RoleValidator } from 'components';
import { USER_SERVICES, PMS_SERVICES } from 'Services';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import GroupUtils from 'Shared/GroupUtils';
import PmsDropdown from 'Views/Common/PmsDropdown';
import PatientConsent from 'Views/Common/PatientConsent';
import {
  FIRM_NAME_MAPPING,
  isLocationLessPmsVendor,
} from 'Views/Common/enum';
import { SUPER_ADMIN, SUBSCRIBILI_USER } from 'global-constants/roles';
import styles from './Capabilities.module.scss';

const getMultiFrequencyPlanNames = (planList = []) =>
  (planList || [])
    .filter(
      (plan) => !plan?.plan_package_type || plan.plan_package_type === 'Base',
    )
    .filter((plan) => {
      const frequencies = new Set(
        (plan?.pricing_tiers_list || [])
          .filter(
            (tier) =>
              tier?.status === 'Active' && !tier?.legacy_reactivation_allowed,
          )
          .map((tier) => tier?.pricing_tiers?.billing_frequency)
          .filter(Boolean),
      );

      return frequencies.size > 1;
    })
    .map((plan) => plan?.plan_name);

const Capabilities = (props) => {
  const {
    name = '',
    levelData = {},
    autolinking = false,
    patientSearch = false,
    showEmployeeSupport = false,
    showPmsIntegration = false,
    showDuplicateAppts = false,
    showAutoAssignPlan = false,
    showAllowGetAddons = false,
    showAllPlansCombinedToggle = false,
    showFeeScheduleSavingsToggle = false,
    planList = [],
    showAllowFacility = false,
    showSeparateLocation = false,
    showPatientConsent = false,
    showEsignature = false,
    showWalletPass = false,
    showPrivateOnly = false,
    showMembershipCampaignEmail = false,
    showMembershipCampaignSms = false,
    groupSettings = null,
    settings: {
      capture_patient_id = false,
      allow_manual_appt = true,
      pms_auto_link = false,
      duplicate_pms_appt = false,
      allow_facility = false,
      location_separate = false,
      menu_config: { pms_patient_details = false, employee_list = false } = {},
      setting_level,
      pms_vendor = [],
      pms_info = {},
      auto_location_plan = true,
      allow_get_addons = true,
      show_all_plans_combined = false,
      use_fee_schedule_for_savings_calc = false,
      patient_form_info: { vendor = '', config_name = '', form_url = '' } = {},
      signature_enabled = false,
      wallet_pass_enabled = false,
      allow_non_subscriber_email = false,
      allow_non_subscriber_sms = false,
    } = {},
    onUpdate,
  } = props;

  const {
    partner_id,
    group_id,
    region_id,
    location_id,
    status: locationStatus,
    private_only = false,
  } = levelData;
  const [vendor_name] = pms_vendor;
  const vendor_firmname =
    pms_info[FIRM_NAME_MAPPING[vendor_name]] || pms_info?.pms_firm_name;
  const pms_location_id =
    pms_info?.pms_location_id || pms_info?.nexhealth_location_id;

  // When viewing a location, groupSettings is included in the GET /settings/location response.
  // If the group has signature_enabled=false, the location toggle must be shown as disabled.
  const isSignatureDisabledByGroup =
    setting_level === 'location' && groupSettings !== null && groupSettings?.signature_enabled === false;

  // Same three-state rule the backend applies: the group gates the
  // location, so a location cannot turn the card on for a group that
  // has it off. See loop.services.wallet.flags.wallet_enabled.
  const isWalletPassDisabledByGroup =
    setting_level === 'location' && groupSettings !== null && groupSettings?.wallet_pass_enabled === false;

  const { dispatch: globalDispatch } = useContext(Context);

  const [isLoading, setIsLoading] = useState(false);
  const [capturePatientId, setCapturePatientId] = useState(capture_patient_id);
  const [duplicatePmsAppt, setDuplicatePmsAppt] = useState(duplicate_pms_appt);
  const [separateLocation, setSeparateLocation] = useState(location_separate);
  const [pmsAutolink, setPmsAutolink] = useState(pms_auto_link);
  const [allowFacility, setAllowFacility] = useState(allow_facility);
  const [pmsPatientSearch, setPmsPatientSearch] = useState(pms_patient_details);
  const [employeeSupport, setEmployeeSupport] = useState(employee_list);
  const [autoAssignPlan, setAutoAssignPlan] = useState(auto_location_plan);
  const [allowGetAddons, setAllowGetAddons] = useState(allow_get_addons);
  const [showAllPlansCombined, setShowAllPlansCombined] = useState(
    show_all_plans_combined,
  );
  const [useFeeScheduleForSavingsCalc, setUseFeeScheduleForSavingsCalc] =
    useState(use_fee_schedule_for_savings_calc);
  const [signatureEnabled, setSignatureEnabled] = useState(signature_enabled);
  const [walletPassEnabled, setWalletPassEnabled] = useState(wallet_pass_enabled);
  const [privateOnly, setPrivateOnly] = useState(private_only);

  const [vendorsList, setVendorsList] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorName, setVendorName] = useState(vendor_name);
  const [vendorFirmname, setVendorFirmname] = useState(vendor_firmname);
  const [validateLoading, setValidateLoading] = useState(false);
  const [pmsLocationId, setPmsLocationId] = useState(pms_location_id);
  const [pmsLocationDisplayId, setPmsLocationDisplayId] = useState(
    pms_info?.pms_location_display_id || null,
  );
  // Relay identity fields; hydrated from the stored pms_info so a later
  // settings save re-sends them instead of wiping them.
  const [pmsSystem, setPmsSystem] = useState(pms_info?.pms_system || null);
  const [pmsScope, setPmsScope] = useState(pms_info?.pms_scope || null);
  const [pmsDsoId, setPmsDsoId] = useState(pms_info?.dso_id || null);
  const [validateLocationLoading, setValidateLocationLoading] = useState(false);
  const [reqFields, setReqFields] = useState(() => {
    if (!capture_patient_id) return {};
    const existingPmsLocationId =
      pms_info?.pms_location_id || pms_info?.nexhealth_location_id;
    return {
      vendor_firmname: { isValid: true, error: false, text: '' },
      ...(existingPmsLocationId
        ? {
            pms_location_id: {
              isValid: true,
              error: false,
              text: '',
              value: existingPmsLocationId,
            },
          }
        : {}),
    };
  });
  const [instanceType, setInstanceType] = useState(() => {
    const existingPmsLocationId =
      pms_info?.pms_location_id || pms_info?.nexhealth_location_id;
    if (!existingPmsLocationId) return '';
    return existingPmsLocationId === '0' ? 'standalone' : 'clinic';
  });

  const [consentVendorsLoading, setConsentVendorsLoading] = useState(false);
  const [consentVendorsList, setConsentVendorsList] = useState([]);
  const [enablePatientConsent, setEnablePatientConsent] = useState(
    Boolean(vendor),
  );
  const [enableManualAppointment, setEnableManualAppointment] =
    useState(allow_manual_appt);
  const [consentVendorName, setConsentVendorName] = useState(vendor);
  const [consentConfigName, setConsentConfigName] = useState(config_name);
  const [consentFormUrl, setConsentFormUrl] = useState(form_url);

  const handleShowAllPlansCombinedChange = async (event) => {
    const { checked } = event.target;

    if (checked) {
      const multiFrequencyPlanNames = getMultiFrequencyPlanNames(planList);
      if (multiFrequencyPlanNames.length) {
        dispatchGlobalNotification(
          'error',
          `Combine Billing Frequency can't be turned on while these active plans offer more than one billing frequency: ${multiFrequencyPlanNames.join(', ')}`,
        );
        return;
      }
    }

    setShowAllPlansCombined(checked);
    const isSaved = await saveSettingsData(
      'showAllPlansCombined',
      checked,
    );
    if (!isSaved) {
      setShowAllPlansCombined(!checked);
    }
  };

  // Every switch that does nothing but "set the state, then save it",
  // as [setter, saveSettingsData type]. Adding the wallet pass as a
  // fifteenth `else if` took handleSwitchChange past the cognitive
  // complexity gate; the chain below now holds only the two switches
  // that genuinely differ.
  const savedSwitches = {
    duplicatePmsAppt: [setDuplicatePmsAppt, 'appointment'],
    pmsAutolink: [setPmsAutolink, 'link'],
    allowFacility: [setAllowFacility, 'allowFacility'],
    membershipCampaignEmail: [null, 'membershipCampaignEmail'],
    membershipCampaignSms: [null, 'membershipCampaignSms'],
    separateLocation: [setSeparateLocation, 'separateLocation'],
    pmsPatientSearch: [setPmsPatientSearch, 'search'],
    employeeSupport: [setEmployeeSupport, 'employee'],
    autoAssignPlan: [setAutoAssignPlan, 'autoAssignPlan'],
    allowGetAddons: [setAllowGetAddons, 'allowGetAddons'],
    enableManualAppointment: [
      setEnableManualAppointment,
      'enableManualAppointment',
    ],
    signatureEnabled: [setSignatureEnabled, 'signatureEnabled'],
    walletPassEnabled: [setWalletPassEnabled, 'walletPassEnabled'],
    useFeeScheduleForSavingsCalc: [
      setUseFeeScheduleForSavingsCalc,
      'useFeeScheduleForSavingsCalc',
    ],
  };

  const handleSwitchChange = async (event) => {
    const { name: eName, checked } = event.target;

    // The two that do not save here: capture_patient_id and the
    // consent flag are written by the page's own save, not per toggle.
    if (eName === 'capturePatientId') {
      setCapturePatientId(checked);

      if (checked) {
        setReqFields((prev) => ({
          ...prev,
          vendor_name: {
            value: '',
            error: false,
            isValid: false,
            text: '',
          },
        }));
      }
      setVendorName('');
      setVendorFirmname('');
      return;
    }

    if (eName === 'enablePatientConsent') {
      setEnablePatientConsent(checked);
      return;
    }

    const saved = savedSwitches[eName];
    if (!saved) {
      return;
    }

    const [setValue, type] = saved;
    if (setValue) {
      setValue(checked);
    }
    const isSaved = await saveSettingsData(type, checked);
    if (!isSaved && setValue) {
      setValue(!checked);
    }
  };

  const levelPayload = {
    [setting_level]: {
      location_id,
      region_id,
      group_id,
      partner_id,
    },
  };

  const saveSettingsData = async (type = 'capture', value = '') => {
    setIsLoading(type);
    let isSaved = false;

    try {
      let settingsData = {};

      if (type === 'capture') {
        if (!capture_patient_id && capture_patient_id !== capturePatientId) {
          // Enabling PMS capture for the first time
          settingsData = {
            capture_patient_id: true,
            pms_vendor: [vendorName],
            pms_info: {
              [FIRM_NAME_MAPPING[vendorName] || 'pms_firm_name']: vendorFirmname,
            },
          };

          // Firm-scoped vendors (e.g. Twelve) do not use a location; omit the
          // location id and its display id so the backend leaves it unmapped
          // instead of storing "0". Other standalone vendors still send "0".
          if (pmsLocationId && !isLocationLessPmsVendor(vendorName)) {
            settingsData.pms_info.pms_location_id = pmsLocationId;
          }

          if (pmsLocationDisplayId && !isLocationLessPmsVendor(vendorName)) {
            settingsData.pms_info.pms_location_display_id =
              pmsLocationDisplayId;
          }

          // Relay identity fields persist regardless of location scope.
          if (pmsSystem) {
            settingsData.pms_info.pms_system = pmsSystem;
          }
          if (pmsScope) {
            settingsData.pms_info.pms_scope = pmsScope;
          }
          if (pmsDsoId) {
            settingsData.pms_info.dso_id = pmsDsoId;
          }
        } else if (capture_patient_id) {
          // PMS already enabled — updating vendor/firm/location
          settingsData = {
            pms_vendor: [vendorName],
            pms_info: {
              [FIRM_NAME_MAPPING[vendorName] || 'pms_firm_name']: vendorFirmname,
            },
          };

          // Firm-scoped vendors (e.g. Twelve) do not use a location; omit the
          // location id and its display id so the backend leaves it unmapped
          // instead of storing "0". Other standalone vendors still send "0".
          if (pmsLocationId && !isLocationLessPmsVendor(vendorName)) {
            settingsData.pms_info.pms_location_id = pmsLocationId;
          }

          if (pmsLocationDisplayId && !isLocationLessPmsVendor(vendorName)) {
            settingsData.pms_info.pms_location_display_id =
              pmsLocationDisplayId;
          }

          // Relay identity fields persist regardless of location scope.
          if (pmsSystem) {
            settingsData.pms_info.pms_system = pmsSystem;
          }
          if (pmsScope) {
            settingsData.pms_info.pms_scope = pmsScope;
          }
          if (pmsDsoId) {
            settingsData.pms_info.dso_id = pmsDsoId;
          }
        }
      } else if (type === 'consent') {
        if (enablePatientConsent) {
          settingsData = {
            patient_form_info: {
              vendor: consentVendorName,
              config_name: consentConfigName,
              form_url: consentFormUrl,
            },
          };
        } else {
          settingsData.patient_form_info = null;
        }
      } else if (type === 'appointment') {
        settingsData = {
          duplicate_pms_appt: value,
        };
      } else if (type === 'link') {
        settingsData = {
          pms_auto_link: value,
        };
      } else if (type === 'allowFacility') {
        settingsData = {
          allow_facility: value,
        };
      } else if (type === 'membershipCampaignEmail') {
        settingsData = {
          allow_non_subscriber_email: value,
        };
      } else if (type === 'membershipCampaignSms') {
        settingsData = {
          allow_non_subscriber_sms: value,
        };
      } else if (type === 'separateLocation') {
        settingsData = {
          location_separate: value,
        };
      } else if (type === 'search') {
        settingsData = {
          menu_config: {
            pms_patient_details: value,
          },
        };
      } else if (type === 'employee') {
        settingsData = {
          menu_config: {
            employee_list: value,
          },
        };
      } else if (type === 'autoAssignPlan') {
        settingsData = {
          auto_location_plan: value,
        };
      } else if (type === 'allowGetAddons') {
        settingsData = {
          allow_get_addons: value,
        };
      } else if (type === 'showAllPlansCombined') {
        settingsData = {
          show_all_plans_combined: value,
        };
      } else if (type === 'enableManualAppointment') {
        settingsData = {
          allow_manual_appt: value,
        };
      } else if (type === 'signatureEnabled') {
        settingsData = {
          signature_enabled: value,
        };
      } else if (type === 'walletPassEnabled') {
        settingsData = {
          wallet_pass_enabled: value,
        };
      } else if (type === 'useFeeScheduleForSavingsCalc') {
        settingsData = {
          use_fee_schedule_for_savings_calc: value,
        };
      }

      const payload = { ...levelPayload, setting: settingsData };

      const response = await USER_SERVICES.updateLevels(payload, setting_level);

      if (response?.type === 'success') {
        dispatchGlobalNotification(
          'success',
          'Capability updated successfully',
        );
        isSaved = true;
        onUpdate && onUpdate();
      } else {
        throw response?.message || response?.error;
      }
    } catch (error) {
      dispatchGlobalNotification(
        'error',
        error || 'Error updating the capability details',
      );
    }

    setIsLoading(false);
    return isSaved;
  };

  const onVendorSelect = (evt) => {
    const vendorVal = evt?.target?.value || '';

    let locReqFields = {};
    if (
      ['nexhealth', 'opendental', 'athena', 'denticon', 'carestack', 'relay'].includes(
        vendorVal,
      )
    ) {
      locReqFields = {
        pms_location_id: {
          value: '',
          error: false,
          isValid: false,
          text: '',
        },
      };
    }

    setVendorName(vendorVal);
    setVendorFirmname('');
    setInstanceType('');
    setPmsLocationId('');
    // Clear relay identity so a stale value is not carried to a new vendor.
    setPmsLocationDisplayId(null);
    setPmsSystem(null);
    setPmsScope(null);
    setPmsDsoId(null);
    setReqFields((prev) => ({
      ...prev,
      vendor_name: {
        ...prev.vendor_name,
        value: vendorVal,
        error: !vendorVal,
        isValid: Boolean(vendorVal),
        text: !vendorVal ? 'Please choose a vendor' : '',
      },
      vendor_firmname: {
        value: '',
        error: false,
        isValid: false,
        text: '',
      },
      ...locReqFields,
    }));
  };

  const onConsentVendorSelect = (evt) => {
    const vendorVal = evt?.target?.value || '';

    setConsentVendorName(vendorVal);
    setReqFields((prev) => ({
      ...prev,
      vendor: {
        ...prev.vendor,
        value: vendorVal,
        error: !vendorVal,
        isValid: Boolean(vendorVal),
        text: !vendorVal ? 'Please choose a vendor' : '',
      },
    }));
  };

  const onConsentConfignameChange = (evt) => {
    const configValue = evt?.target?.value;

    setConsentConfigName(configValue);
    setReqFields((prev) => ({
      ...prev,
      config_name: {
        ...prev.config_name,
        value: configValue,
        error: !configValue,
        isValid: !!configValue,
        text: 'Config name is required',
      },
    }));
  };

  const onConsentFormUrlChange = (evt) => {
    const formValue = evt?.target?.value;

    setConsentFormUrl(formValue);
    setReqFields((prev) => ({
      ...prev,
      form_url: {
        ...prev.form_url,
        value: formValue,
        error: !formValue,
        isValid: !!formValue,
        text: 'Form Url is required',
      },
    }));
  };

  const onFirmnameChange = (evt) => {
    const firmValue = evt?.target?.value;

    setVendorFirmname(firmValue);
    setReqFields((prev) => ({
      ...prev,
      vendor_firmname: {
        ...prev.vendor_firmname,
        value: firmValue,
        error: false,
        isValid: false,
        text: '',
      },
      pms_location_id: {
        ...prev.pms_location_id,
        error: false,
        isValid: false,
        text: '',
      },
    }));
  };

  const onLocIdChange = (evt) => {
    const locationIdValue = evt?.target?.value;

    setPmsLocationId(locationIdValue);
    setReqFields((prev) => ({
      ...prev,
      pms_location_id: {
        ...prev.pms_location_id,
        value: locationIdValue,
        error: false,
        isValid: false,
        text: '',
      },
    }));
  };

  const onInstanceTypeChange = (value) => {
    setInstanceType(value);
    // An instance-type change invalidates any prior location validation, so
    // clear the captured relay identity — otherwise switching a validated
    // Relay clinic to Standalone saves pms_location_id '0' with the previous
    // clinic's pms_system / dso_id.
    setPmsLocationDisplayId(null);
    setPmsSystem(null);
    setPmsScope(null);
    setPmsDsoId(null);
    if (value === 'standalone') {
      setPmsLocationId('0');
      setReqFields((prev) => ({
        ...prev,
        pms_location_id: {
          ...prev.pms_location_id,
          value: '0',
          error: false,
          isValid: true,
          text: '',
        },
      }));
    } else {
      setPmsLocationId('');
      setReqFields((prev) => ({
        ...prev,
        pms_location_id: {
          ...prev.pms_location_id,
          value: '',
          error: false,
          isValid: false,
          text: '',
        },
      }));
    }
  };

  const onFirmSelect = (evt) => {
    const firmValue = evt?.target?.value;
    setVendorFirmname(firmValue);
    setInstanceType('');
    setPmsLocationId('');
    // Clear relay identity; it is re-captured on the next location validate.
    setPmsLocationDisplayId(null);
    setPmsSystem(null);
    setPmsScope(null);
    setPmsDsoId(null);
    setReqFields((prev) => ({
      ...prev,
      vendor_firmname: {
        ...prev.vendor_firmname,
        value: firmValue,
        error: false,
        isValid: Boolean(firmValue),
      },
      pms_location_id: {
        ...prev.pms_location_id,
        error: false,
        isValid: false,
        text: '',
      },
    }));
  };

  const handleValidateFirmname = async () => {
    setValidateLoading(true);
    try {
      if (vendorFirmname && vendorFirmname.length > 0) {
        const key = FIRM_NAME_MAPPING[vendorName] || 'pms_firm_name';
        const payload = {
          vendor: vendorName,
          [key]: vendorFirmname,
        };

        const vResponse = await PMS_SERVICES.validatePms(payload);

        if (vResponse?.type === 'success') {
          const respData = vResponse?.data;

          if (
            respData?.location_required !== undefined &&
            !respData?.location_required
          ) {
            const defaultPMSLocationId = '0';
            setPmsLocationId(defaultPMSLocationId);
            setReqFields((prev) => ({
              ...prev,
              vendor_firmname: {
                ...prev.vendor_firmname,
                error: false,
                isValid: true,
              },
              pms_location_id: {
                ...prev.pms_location_id,
                value: defaultPMSLocationId,
                error: false,
                isValid: true,
                text: '',
              },
            }));
          } else {
            setReqFields((prev) => ({
              ...prev,
              vendor_firmname: {
                ...prev.vendor_firmname,
                error: false,
                isValid: true,
              },
            }));
          }
        } else {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      console.log(e);

      setReqFields((prev) => ({
        ...prev,
        vendor_firmname: {
          ...prev.vendor_firmname,
          error: true,
          isValid: false,
          text: 'Enter a valid firm name',
        },
      }));
    }
    setValidateLoading(false);
  };

  const handleValidateLocationId = async () => {
    if (!group_id || !partner_id) return;

    setValidateLocationLoading(true);
    try {
      if (pmsLocationId) {
        const payload = {
          vendor: vendorName,
          pms_firm_name: vendorFirmname,
          pms_location_id: pmsLocationId,
        };
        const vResponse = await PMS_SERVICES.validatePms(payload);

        if (vResponse?.type === 'success') {
          const respData = vResponse?.data;
          if (respData?.pms_location_display_id) {
            setPmsLocationDisplayId(respData.pms_location_display_id);
          }
          // Capture the relay identity fields so they persist in pms_info.
          // Set unconditionally so a non-relay revalidate clears stale values.
          setPmsSystem(respData?.pms_system || null);
          setPmsScope(respData?.pms_scope || null);
          setPmsDsoId(respData?.dso_id || null);
          setReqFields((prev) => ({
            ...prev,
            pms_location_id: {
              ...prev.pms_location_id,
              error: false,
              isValid: true,
            },
          }));
        } else {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      console.log(e);

      setReqFields((prev) => ({
        ...prev,
        pms_location_id: {
          ...prev.pms_location_id,
          error: true,
          isValid: false,
          text: 'Enter valid location id',
        },
      }));
    }
    setValidateLocationLoading(false);
  };

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const fetchVendorsList = async () => {
    setVendorsLoading(true);
    try {
      const vResponse = await PMS_SERVICES.getVendorsList();

      if (vResponse?.type === 'success') {
        setVendorsList(vResponse?.data?.vendors);
      } else {
        console.log('Error fetching vendors details');
      }
    } catch (e) {
      console.log(e);
    }
    setVendorsLoading(false);
  };

  const fetchConsentVendorsList = async () => {
    setConsentVendorsLoading(true);

    try {
      const vResponse = await PMS_SERVICES.getConsentVendorsList();

      if (vResponse?.type === 'success' && vResponse?.data) {
        setConsentVendorsList(vResponse?.data?.vendors);
      } else {
        throw vResponse?.message || vResponse?.Error;
      }
    } catch (e) {
      console.log(e || 'Error fetching vendors details');

      setConsentVendorsList([]);
    } finally {
      setConsentVendorsLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorsList();
    fetchConsentVendorsList();
  }, []);

  useEffect(() => {
    setPmsAutolink(pms_auto_link);
  }, [pms_auto_link]);

  useEffect(() => {
    setAllowFacility(allow_facility);
  }, [allow_facility]);

  useEffect(() => {
    setSeparateLocation(location_separate);
  }, [location_separate]);

  useEffect(() => {
    setDuplicatePmsAppt(duplicate_pms_appt);
  }, [duplicate_pms_appt]);

  useEffect(() => {
    setPmsPatientSearch(pms_patient_details);
  }, [pms_patient_details]);

  useEffect(() => {
    capture_patient_id && setCapturePatientId(capture_patient_id);
    vendor_name && setVendorName(vendor_name);
    vendor_firmname && setVendorFirmname(vendor_firmname);
    pms_location_id && setPmsLocationId(pms_location_id);
    pms_info?.pms_location_display_id &&
      setPmsLocationDisplayId(pms_info.pms_location_display_id);
    pms_info?.pms_system && setPmsSystem(pms_info.pms_system);
    pms_info?.pms_scope && setPmsScope(pms_info.pms_scope);
    pms_info?.dso_id && setPmsDsoId(pms_info.dso_id);
  }, [capture_patient_id]);

  useEffect(() => {
    vendor && setEnablePatientConsent(Boolean(vendor));
    vendor && setConsentVendorName(vendor);
    config_name && setConsentConfigName(config_name);
    form_url && setConsentFormUrl(form_url);
  }, [vendor]);

  useEffect(() => {
    setAllowGetAddons(allow_get_addons);
  }, [allow_get_addons]);

  useEffect(() => {
    setShowAllPlansCombined(show_all_plans_combined);
  }, [show_all_plans_combined]);

  useEffect(() => {
    setUseFeeScheduleForSavingsCalc(use_fee_schedule_for_savings_calc);
  }, [use_fee_schedule_for_savings_calc]);

  useEffect(() => {
    setSignatureEnabled(signature_enabled);
  }, [signature_enabled]);

  useEffect(() => {
    setWalletPassEnabled(wallet_pass_enabled);
  }, [wallet_pass_enabled]);

  useEffect(() => {
    setPrivateOnly(private_only);
  }, [private_only]);

  const handlePrivateOnlyChange = async (event) => {
    const { checked } = event.target;

    setIsLoading('privateOnly');
    setPrivateOnly(checked);

    try {
      const payload = {
        location: {
          location_id,
          region_id,
          group_id,
          partner_id,
          private_only: checked,
        },
      };

      const response = await USER_SERVICES.updateLevels(payload, 'location');

      if (response?.type === 'success') {
        onUpdate?.();
      } else {
        setPrivateOnly(!checked);
      }
    } catch {
      setPrivateOnly(!checked);
    }

    setIsLoading(false);
  };

  const renderTooltip = (message) => (
    <Tooltip title={message}>
      <InfoIcon
        color="primary"
        data-testid="heading-info-button"
        fontSize="small"
        className="ml-5 cursor-pointer"
      />
    </Tooltip>
  );

  const checkIfPmsTouched = () => {
    const toggleChanged = capture_patient_id !== capturePatientId;
    const vendorChanged = capturePatientId && (
      vendorName !== vendor_name ||
      vendorFirmname !== vendor_firmname ||
      pmsLocationId !== pms_location_id
    );

    const formTouched = Boolean(
      (toggleChanged && vendorName && vendorFirmname) || vendorChanged,
    );

    if (!formTouched) return false;

    if (!reqFields['vendor_firmname']?.isValid) return false;

    if (Boolean(setting_level === 'location') && !instanceType) return false;

    if (
      Boolean(setting_level === 'location') &&
      instanceType === 'clinic' &&
      !reqFields['pms_location_id']?.isValid
    ) {
      return false;
    }

    return true;
  };

  const checkConsentFormTouched = () => {
    let formTouched = false;

    if (!enablePatientConsent) {
      formTouched = Boolean(vendor) !== enablePatientConsent;
    } else {
      formTouched =
        consentVendorName &&
        consentConfigName &&
        consentFormUrl &&
        (consentVendorName !== vendor ||
          consentConfigName !== config_name ||
          consentFormUrl !== form_url);
    }

    return formTouched;
  };

  const pmsUpdated = checkIfPmsTouched();
  const consentFormUpdated = checkConsentFormTouched();

  const resetSettingsData = () => {
    setCapturePatientId(capture_patient_id);
    vendor_name && setVendorName(vendor_name);
    vendor_firmname && setVendorFirmname(vendor_firmname);
    pms_location_id && setPmsLocationId(pms_location_id);
    // Restore relay identity from the stored setting so Cancel discards any
    // in-progress capture instead of leaving a stale value.
    setPmsLocationDisplayId(pms_info?.pms_location_display_id || null);
    setPmsSystem(pms_info?.pms_system || null);
    setPmsScope(pms_info?.pms_scope || null);
    setPmsDsoId(pms_info?.dso_id || null);
    let resetInstanceType = '';
    if (pms_location_id) {
      resetInstanceType = pms_location_id === '0' ? 'standalone' : 'clinic';
    }
    setInstanceType(resetInstanceType);

    setEnablePatientConsent(Boolean(vendor));
    setConsentVendorName(vendor);
    setConsentConfigName(config_name);
    setConsentFormUrl(form_url);
  };

  return (
    <SectionWrapper
      title={`${name} Capabilities`}
      rightSection={
        <>
          {(pmsUpdated || consentFormUpdated) && (
            <>
              <Button
                size="small"
                onClick={() =>
                  saveSettingsData(pmsUpdated ? 'capture' : 'consent')
                }
                disabled={isLoading}
                data-testid="save-capability-changes-button"
                startIcon={
                  isLoading === 'consent' ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <SaveIcon color="secondary" />
                  )
                }
              >
                <Typography color="secondary">Save Changes</Typography>
              </Button>
              <Button
                size="small"
                className="mg_left_8"
                onClick={resetSettingsData}
                disabled={isLoading}
                data-testid="cancel-capability-changes-button"
                startIcon={
                  <HighlightOffIcon
                    disabled={isLoading}
                    className="theme_color_orange"
                  />
                }
              >
                <Typography className="theme_color_orange">Cancel</Typography>
              </Button>
            </>
          )}
        </>
      }
    >
      <Grid container spacing={2} alignItems="stretch">
        {showPmsIntegration && (
          <Grid item xs={12} className="d-flex align-center ">
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={4} lg={3} className="d-flex align-center ">
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={styles.toggleTitle}
                >
                  PMS Integration Enabled
                </Typography>

                <ToggleTickSwitch
                  className="mg_left_8"
                  checked={capturePatientId}
                  onChange={handleSwitchChange}
                  disabled={Boolean(isLoading) || capture_patient_id}
                  name="capturePatientId"
                  inputProps={{
                    'aria-label': 'secondary checkbox',
                    'data-testid': 'toggle-capture-patient',
                  }}
                />
              </Grid>

              {capturePatientId && (
                <Grid
                  item
                  xs={12}
                  md={8}
                  lg={9}
                  className="d-flex align-center "
                >
                  <PmsDropdown
                    isLoading={vendorsLoading}
                    data={vendorsList}
                    vendorName={vendorName}
                    onVendorSelect={onVendorSelect}
                    vendorFirmname={vendorFirmname}
                    onFirmnameChange={onFirmnameChange}
                    onFirmSelect={onFirmSelect}
                    handleValidateFirmname={handleValidateFirmname}
                    validateLoading={validateLoading}
                    captureLocationId={Boolean(setting_level === 'location')}
                    pmsLocationId={pmsLocationId}
                    pmsLocationDisplayId={pmsLocationDisplayId}
                    onLocIdChange={onLocIdChange}
                    handleValidateLocationId={handleValidateLocationId}
                    validateLocationLoading={validateLocationLoading}
                    reqFields={reqFields}
                    disabled={capture_patient_id || Boolean(isLoading)}
                    instanceType={instanceType}
                    onInstanceTypeChange={onInstanceTypeChange}
                    onLocationValidated={({
                      locationId,
                      displayId,
                      pmsSystem,
                      pmsScope,
                      dsoId,
                      isValid,
                    }) => {
                      if (displayId) setPmsLocationDisplayId(displayId);
                      // On a valid response persist the relay identity trio
                      // (null clears a stale value for non-relay vendors).
                      if (isValid) {
                        setPmsSystem(pmsSystem || null);
                        setPmsScope(pmsScope || null);
                        setPmsDsoId(dsoId || null);
                      }
                      setReqFields((prev) => ({
                        ...prev,
                        pms_location_id: {
                          ...prev.pms_location_id,
                          value: locationId,
                          error: !isValid,
                          isValid,
                          text: isValid ? '' : 'Not a valid location',
                        },
                      }));
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {showDuplicateAppts && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Subscription Payment Appointment
              {renderTooltip(
                'Creating appointment to record membership payment',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={duplicatePmsAppt}
              onChange={handleSwitchChange}
              name="duplicatePmsAppt"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Toggle PMS duplicate appointment',
                'data-testid': 'toggle-duplicate-pms-appt',
              }}
            />

            {Boolean(isLoading === 'appointment') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showSeparateLocation && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Location-Based Email
              {renderTooltip(
                'Adds the location name to subscription emails. Contact details always come from the group.',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={separateLocation}
              onChange={handleSwitchChange}
              name="separateLocation"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Allow Separate Location option toggle',
                'data-testid': 'toggle-allow-separate-location',
              }}
            />

            {Boolean(isLoading === 'separateLocation') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {autolinking && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              PMS Auto-linking Enabled
              {renderTooltip(
                'Link patients to PMS upon signing up via the patient portal',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={pmsAutolink}
              onChange={handleSwitchChange}
              name="pmsAutolink"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'toggle pms checkbox',
                'data-testid': 'toggle-pms-autolink',
              }}
            />

            {Boolean(isLoading === 'link') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {patientSearch && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={styles.toggleTitle}
            >
              All-patient Appointment Lookup
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={pmsPatientSearch}
              onChange={handleSwitchChange}
              name="pmsPatientSearch"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'PMS Patient Search checkbox',
                'data-testid': 'toggle-pms-patient-search',
              }}
            />

            {Boolean(isLoading === 'search') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showEmployeeSupport && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={styles.toggleTitle}
            >
              Enable Employee Subscription
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={employeeSupport}
              onChange={handleSwitchChange}
              name="employeeSupport"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Employee Support checkbox',
                'data-testid': 'toggle-employee-support',
              }}
            />

            {Boolean(isLoading === 'employee') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showAutoAssignPlan && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Auto Assign Plans to Location
              {renderTooltip(
                'Auto-assigning all plans from the group to all locations under them',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={autoAssignPlan}
              onChange={handleSwitchChange}
              name="autoAssignPlan"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Auto assign plans to location toggle',
                'data-testid': 'toggle-auto-assign-plan',
              }}
            />

            {Boolean(isLoading === 'autoAssignPlan') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showAllowGetAddons && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={styles.toggleTitle}
            >
              Allow Get-addons option
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={allowGetAddons}
              onChange={handleSwitchChange}
              name="allowGetAddons"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Allow Get-addons option toggle',
                'data-testid': 'toggle-allow-get-addons',
              }}
            />

            {Boolean(isLoading === 'allowGetAddons') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showAllowFacility && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Allow Facility
              {renderTooltip(
                'Option to capture facility ID when creating a subscriber',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={allowFacility}
              onChange={handleSwitchChange}
              name="allowFacility"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Allow Facility option toggle',
                'data-testid': 'toggle-allow-facility',
              }}
            />

            {Boolean(isLoading === 'allowFacility') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showEsignature && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Enable E-Signature
              {renderTooltip(
                isSignatureDisabledByGroup
                  ? 'E-Signature is disabled at the group level'
                  : 'Enable e-signature for checkout flows.',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={isSignatureDisabledByGroup ? false : signatureEnabled}
              onChange={handleSwitchChange}
              name="signatureEnabled"
              disabled={Boolean(isLoading) || isSignatureDisabledByGroup}
              inputProps={{
                'aria-label': 'Enable E-Signature toggle',
                'data-testid': 'toggle-signature-enabled',
              }}
            />

            {Boolean(isLoading === 'signatureEnabled') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showWalletPass && (
          <RoleValidator roles={[SUPER_ADMIN, SUBSCRIBILI_USER]}>
            <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
              <Typography
                variant="body1"
                color="textSecondary"
                className={`${styles.toggleTitle} d-flex align-center`}
              >
                Enable Wallet Pass
                {renderTooltip(
                  isWalletPassDisabledByGroup
                    ? 'Wallet pass is disabled at the group level'
                    : 'Offer a Google or Apple Wallet membership card. Upload the card artwork before enabling.',
                )}
              </Typography>

              <ToggleTickSwitch
                className="mg_left_8"
                checked={
                  isWalletPassDisabledByGroup ? false : walletPassEnabled
                }
                onChange={handleSwitchChange}
                name="walletPassEnabled"
                disabled={Boolean(isLoading) || isWalletPassDisabledByGroup}
                inputProps={{
                  'aria-label': 'Enable Wallet Pass toggle',
                  'data-testid': 'toggle-wallet-pass-enabled',
                }}
              />

              {Boolean(isLoading === 'walletPassEnabled') && (
                <CircularProgress size={20} color="primary" />
              )}
            </Grid>
          </RoleValidator>
        )}

        {showPrivateOnly && (
          <RoleValidator roles={[SUPER_ADMIN, SUBSCRIBILI_USER]}>
            <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
              <Typography
                variant="body1"
                color="textSecondary"
                className={`${styles.toggleTitle} d-flex align-center`}
              >
                In Office Only
                {renderTooltip(
                  'Restricts this location to Provider Portal enrollments only, hiding it from the Patient Portal',
                )}
              </Typography>

              <ToggleTickSwitch
                className="mg_left_8"
                checked={privateOnly}
                onChange={handlePrivateOnlyChange}
                name="privateOnly"
                disabled={Boolean(isLoading) || locationStatus !== 'Active'}
                inputProps={{
                  'aria-label': 'In Office Only toggle',
                  'data-testid': 'toggle-private-only',
                }}
              />

              {Boolean(isLoading === 'privateOnly') && (
                <CircularProgress size={20} color="primary" />
              )}
            </Grid>
          </RoleValidator>
        )}

        {showPatientConsent && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Enable Manual Appointment
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={enableManualAppointment}
              onChange={handleSwitchChange}
              disabled={isLoading}
              name="enableManualAppointment"
              inputProps={{
                'aria-label': 'secondary checkbox',
                'data-testid': 'toggle-manual-appointment',
              }}
            />

            {Boolean(isLoading === 'enableManualAppointment') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showMembershipCampaignEmail && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Membership Campaign Email
              {renderTooltip(
                'Send uninsured patients an appointment reminder inviting them to enroll in a membership plan, over email.',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={allow_non_subscriber_email}
              onChange={handleSwitchChange}
              name="membershipCampaignEmail"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Membership Campaign Email toggle',
                'data-testid': 'toggle-membership-campaign-email',
              }}
            />

            {Boolean(isLoading === 'membershipCampaignEmail') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showMembershipCampaignSms && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Membership Campaign SMS
              {renderTooltip(
                'Send uninsured patients an appointment reminder inviting them to enroll in a membership plan, over SMS.',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={allow_non_subscriber_sms}
              onChange={handleSwitchChange}
              name="membershipCampaignSms"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Membership Campaign SMS toggle',
                'data-testid': 'toggle-membership-campaign-sms',
              }}
            />

            {Boolean(isLoading === 'membershipCampaignSms') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showPatientConsent && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Patient Consent Form
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={enablePatientConsent}
              onChange={handleSwitchChange}
              disabled={isLoading}
              name="enablePatientConsent"
              inputProps={{
                'aria-label': 'secondary checkbox',
                'data-testid': 'toggle-patient-consent',
              }}
            />
          </Grid>
        )}

        {showAllPlansCombinedToggle && (
          <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
            <Typography
              variant="body1"
              color="textSecondary"
              className={`${styles.toggleTitle} d-flex align-center`}
            >
              Combine Billing Frequency
              {renderTooltip(
                'When enabled, all plans are displayed together in the Patient Portal without a billing frequency toggle.',
              )}
            </Typography>

            <ToggleTickSwitch
              className="mg_left_8"
              checked={showAllPlansCombined}
              onChange={handleShowAllPlansCombinedChange}
              name="showAllPlansCombined"
              disabled={Boolean(isLoading)}
              inputProps={{
                'aria-label': 'Combine Billing Frequency toggle',
                'data-testid': 'toggle-show-all-plans-combined',
              }}
            />

            {Boolean(isLoading === 'showAllPlansCombined') && (
              <CircularProgress size={20} color="primary" />
            )}
          </Grid>
        )}

        {showFeeScheduleSavingsToggle && (
          <RoleValidator roles={[SUPER_ADMIN, SUBSCRIBILI_USER]}>
            <Grid item xs={12} sm={6} md={3} className="d-flex align-center ">
              <Typography
                variant="body1"
                color="textSecondary"
                className={`${styles.toggleTitle} d-flex align-center`}
              >
                Use Fee Schedule for Savings Calculator
                {renderTooltip(
                  "When enabled, calculates patient savings using the location's stored UCR fees and assigned discounted fee schedule.",
                )}
              </Typography>

              <ToggleTickSwitch
                className="mg_left_8"
                checked={useFeeScheduleForSavingsCalc}
                onChange={handleSwitchChange}
                name="useFeeScheduleForSavingsCalc"
                disabled={Boolean(isLoading)}
                inputProps={{
                  'aria-label': 'Use Fee Schedule for Savings Calculator toggle',
                  'data-testid': 'toggle-use-fee-schedule-for-savings-calc',
                }}
              />

              {Boolean(isLoading === 'useFeeScheduleForSavingsCalc') && (
                <CircularProgress size={20} color="primary" />
              )}
            </Grid>
          </RoleValidator>
        )}

        {showPatientConsent && enablePatientConsent && (
          <Grid item xs={12} className="d-flex align-center ">
            <PatientConsent
              showForm={enablePatientConsent}
              isLoading={consentVendorsLoading}
              data={consentVendorsList}
              consentVendorName={consentVendorName}
              onConsentVendorSelect={onConsentVendorSelect}
              consentConfigName={consentConfigName}
              onConfigNameChange={onConsentConfignameChange}
              consentFormUrl={consentFormUrl}
              onFormUrlChange={onConsentFormUrlChange}
              reqFields={reqFields}
              disabled={isLoading}
            />
          </Grid>
        )}
      </Grid>
    </SectionWrapper>
  );
};

Capabilities.propTypes = {
  name: PropTypes.string,
  levelData: PropTypes.object,
  autolinking: PropTypes.bool,
  patientSearch: PropTypes.bool,
  showEmployeeSupport: PropTypes.bool,
  showAllowFacility: PropTypes.bool,
  showSeparateLocation: PropTypes.bool,
  showPatientConsent: PropTypes.bool,
  showPmsIntegration: PropTypes.bool,
  showDuplicateAppts: PropTypes.bool,
  showAutoAssignPlan: PropTypes.bool,
  showAllowGetAddons: PropTypes.bool,
  showAllPlansCombinedToggle: PropTypes.bool,
  showFeeScheduleSavingsToggle: PropTypes.bool,
  planList: PropTypes.array,
  showEsignature: PropTypes.bool,
  showWalletPass: PropTypes.bool,
  showPrivateOnly: PropTypes.bool,
  showMembershipCampaignEmail: PropTypes.bool,
  showMembershipCampaignSms: PropTypes.bool,
  groupSettings: PropTypes.object,
  settings: PropTypes.object,
  onUpdate: PropTypes.func,
};

export default Capabilities;
